import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { closed } from "../../components/drawer/drawerSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import IconButton from "../../components/common/IconButton";
import { clearVisaCategoryDetails } from "./visacategorySlice";
import {
  createVisaCategory,
  getAllVisaCategories,
  getVisaCategoryByID,
  updateVisaCategory,
} from "./services/visa.services";
import { getAllActiveVisaCountries } from "../visacountry/services/visacountry.services";
import { IVisaCountry } from "../visacountry/visacountryModel";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";

const CreateVisaCategory: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const [formKey, setFormKey] = React.useState(1);

  const VisaCountryList = useAppSelector(
    (state) => state.visacountry.VisaCountryList
  );
  const VisaCategoryDetail = useAppSelector(
    (state) => state.visacategory.VisaCategoryDetail
  );
  const VisaCategoryID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.visacategory.formLoading);

  useEffect(() => {
    dispatch(getAllActiveVisaCountries());

    return () => {
      dispatch(clearVisaCategoryDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [VisaCategoryDetail]);

  useEffect(() => {
    if (VisaCategoryID) {
      dispatch(getVisaCategoryByID(VisaCategoryID));
    }
  }, [VisaCategoryID]);

  const handleSubmit = async (values: any) => {
    try {
      if (!VisaCategoryID) {
        const insertPayload = {
          VisaCategory: values.VisaCategory,
          VisaCountryID: values.VisaCountryID ? +values.VisaCountryID : null,
          ProcessingTime: values.ProcessingTime,
          VisaCost: values.VisaCost,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createVisaCategory(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearVisaCategoryDetails());
          dispatch(getAllVisaCategories());
        }
      } else {
        const editPayload = {
          ID: VisaCategoryID,
          VisaCategory: values.VisaCategory,
          VisaCountryID: values.VisaCountryID ? +values.VisaCountryID : null,
          ProcessingTime: values.ProcessingTime,
          VisaCost: values.VisaCost,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateVisaCategory(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearVisaCategoryDetails());
          dispatch(getAllVisaCategories());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  return (
    <>
      {formLoading && <LoadingPanel gridRef={gridRef} />}
      <Form
        onSubmit={handleSubmit}
        initialValues={VisaCategoryDetail}
        key={formKey}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement style={{ width: "100%" }}>
            <GridLayout
              style={{
                display: "flex",
                flexWrap: "wrap",
              }}
              gap={{ rows: 10 }}
              cols={[{ width: "100%" }]}
            >
              <GridLayoutItem
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  margin: "17px 0 8px",
                }}
              >
                <Typography.h4 className="m-0">
                  {VisaCategoryID
                    ? "Update Visa Category"
                    : "Add Visa Category"}
                </Typography.h4>
                <IconButton
                  type="button"
                  fillMode={"flat"}
                  themeColor={"dark"}
                  style={{ marginRight: 4 }}
                  onClick={() => dispatch(closed())}
                  icon={RxCross1}
                />
              </GridLayoutItem>

              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="VisaCategory"
                  label="Visa Category"
                  placeholder="i.e. Student Visa"
                  component={FormTextField}
                  astrike={true}
                  maxLength={50}
                  validator={requiredValidator}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="VisaCountryID"
                  label="Visa Country"
                  placeholder="i.e. Canada"
                  component={FormSelectionField}
                  validator={requiredValidator}
                  astrike={true}
                  options={VisaCountryList?.map((country: IVisaCountry) => {
                    return {
                      value: country.ID,
                      label: country.VisaCountry,
                    };
                  })}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="ProcessingTime"
                  label="Processing Time"
                  placeholder="i.e. 2 weeks"
                  component={FormTextField}
                  astrike={true}
                  maxLength={50}
                  validator={requiredValidator}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="VisaCost"
                  label="Visa Cost"
                  placeholder="i.e. 2500"
                  component={FormNumericTextField}
                  astrike={true}
                  validator={requiredValidator}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  id={"IsActive"}
                  name={"IsActive"}
                  label={"Status"}
                  placeholder="i.e. Active"
                  component={FormSelectionField}
                  validator={requiredValidator}
                  astrike={true}
                  options={STATUSARRAY?.map((status: any) => {
                    return {
                      value: status.value,
                      label: status.label,
                    };
                  })}
                />
              </GridLayoutItem>

              <GridLayoutItem
                colSpan={3}
                style={{
                  textAlign: "end",
                  marginTop: 20,
                  width: "100%",
                }}
              >
                <ButtonWithLoading
                  label={VisaCategoryID ? "Update" : "Save"}
                  type="submit"
                  disabled={!formRenderProps.allowSubmit || formLoading}
                  loading={formLoading}
                />

                <Button
                  type="button"
                  fillMode={"outline"}
                  themeColor={"primary"}
                  style={{ marginLeft: 10 }}
                  onClick={handleFormClose}
                >
                  Cancel
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateVisaCategory;
